import { useCallback, useMemo, useState } from 'react'
import { useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'

import { useTranslate } from 'lib/tolgee'
import { PanelStore } from 'stores'
import { analyticsTracker } from 'utils'
import { getRoute } from 'utils/routes'
import { PATHNAMES } from 'utils/routes/consts'

import ZaiFakeDoorModal from './components/ZaiFakeDoorModal'
import { DEFAULT_ANSWER_LANGUAGE, QUESTIONS_TRANSLATIONS } from './consts'
import { ZaiChatContext } from './context'
import { getMockedAnswers } from './mock'
import { CREATE_ZAI_SOA_QUESTION_MUTATION } from './query'
import type { AskQuestionOptions, ZaiChatContextData } from './types'

interface ZaiChatProviderProps {
  children: React.ReactNode
}

export function ZaiChatProvider({ children }: ZaiChatProviderProps) {
  const history = useHistory()
  const t = useTranslate()
  const workspaceId = Object(PanelStore?.selectedPanel)?.panelId

  const [isZaiFakeDoorOpen, setIsZaiFakeDoorOpen] = useState(false)
  const [isLoadingFake, setIsLoadingFake] = useState(false)
  const [selectedQuestion, setSelectedQuestion] = useState('')
  const [answer, setAnswer] = useState('')

  const questions = useMemo(() => {
    return Object.entries(QUESTIONS_TRANSLATIONS).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: t(value),
      }),
      {}
    ) as ZaiChatContextData['questions']
  }, [t])

  const [createZaiShareQuestion, { loading: isCreateZaiLoading, error }] = useMutation(
    CREATE_ZAI_SOA_QUESTION_MUTATION,
    {
      onError(error) {
        console.error('ZaiChatProvider @ CreateZaiSoaQuestion >>>', error)
      },
    }
  )

  const askQuestion = useCallback(
    async (question: string, options: AskQuestionOptions = {}) => {
      const { hasFakeDoor, mixpanelProps, brands } = options

      if (mixpanelProps) {
        const { CAT } = analyticsTracker.eventNames
        analyticsTracker.eventTrack(CAT.CLICK_ZAI_QUESTION, { ...mixpanelProps })
      }

      if (hasFakeDoor) {
        setIsZaiFakeDoorOpen(true)
        setSelectedQuestion(question)
        return
      }

      setSelectedQuestion(question)
      const zaiAnswerRoute = getRoute(PATHNAMES.WORKSPACE_ZAI_ANSWER, { panelId: workspaceId })
      history?.push(zaiAnswerRoute)

      const mockedAnswers = getMockedAnswers(t)
      const fakeAnswer = mockedAnswers.find(item => item.question === question)

      if (fakeAnswer) {
        setIsLoadingFake(true)
        await new Promise(resolve => setTimeout(resolve, 5000))
        setIsLoadingFake(false)

        setAnswer(fakeAnswer.answer)
        return
      }

      const firstBrand = brands?.[0]
      const variables = {
        brandContext: brands.map(brand => ({
          contentType: 'all',
          name: brand.name,
          type: brand.own ? 'owner' : 'competitor',
          timeSeries: brand.dailyMetrics.map(metric => ({
            date: metric.date,
            value: metric.score,
          })),
        })),
        filterContext: {
          timeRange: {
            timeUnit: 'day',
            timeValue: 7,
          },
          interval: {},
        },
        category: 'category' in firstBrand ? firstBrand?.category?.name : undefined,
        language: DEFAULT_ANSWER_LANGUAGE,
        message: question,
      }

      const answer = await createZaiShareQuestion({
        variables,
      })

      if (answer?.data?.createZaiSoaQuestion?.message) {
        setAnswer(answer.data.createZaiSoaQuestion.message)
      }
    },
    [createZaiShareQuestion, history, t, workspaceId]
  )

  const isLoading = isCreateZaiLoading || isLoadingFake

  const value = useMemo(
    () => ({ askQuestion, question: selectedQuestion, questions, answer, isLoading, error: !!error }),
    [askQuestion, selectedQuestion, questions, answer, isLoading, error]
  )

  return (
    <ZaiChatContext.Provider value={value}>
      {children}

      <ZaiFakeDoorModal
        isOpen={isZaiFakeDoorOpen}
        closeModal={() => setIsZaiFakeDoorOpen(false)}
        question={selectedQuestion}
      />
    </ZaiChatContext.Provider>
  )
}
